import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  stripeEventStatusId,
  type StripeEventStatusId,
} from './StripeEventStatus.js'

/** Identifier type for public.stripe_event_ */
export type StripeEventId = string & { __brand: 'StripeEventId' }

/** Represents the table public.stripe_event_ */
export default interface StripeEventTable {
  /** Database type: pg_catalog.text */
  id: ColumnType<StripeEventId, StripeEventId, StripeEventId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  type: ColumnType<string, string, string>

  /** Database type: pg_catalog.jsonb */
  payload: ColumnType<unknown, unknown, unknown>

  /** Database type: pg_catalog.text */
  status: ColumnType<
    StripeEventStatusId,
    StripeEventStatusId | undefined,
    StripeEventStatusId
  >

  /** Database type: pg_catalog.text */
  errorMessage: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  attempts: ColumnType<number, number | undefined, number>

  /** Database type: pg_catalog.timestamptz */
  processedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.uuid */
  claimToken: ColumnType<string | null, string | null, string | null>
}

export type StripeEvent = Selectable<StripeEventTable>

export type StripeEventInitializer = Insertable<StripeEventTable>

export type StripeEventMutator = Updateable<StripeEventTable>

export const stripeEventId = z.string() as unknown as z.Schema<StripeEventId>

export const stripeEvent = z.object({
  id: stripeEventId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  type: z.string(),
  payload: z.unknown(),
  status: stripeEventStatusId,
  errorMessage: z.string().nullable().optional(),
  attempts: z.number(),
  processedAt: z.coerce.date().nullable().optional(),
  claimToken: z.string().nullable().optional(),
}) as unknown as z.Schema<StripeEvent>

export const stripeEventInitializer = z.object({
  id: stripeEventId,
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  type: z.string(),
  payload: z.unknown(),
  status: stripeEventStatusId.optional(),
  errorMessage: z.string().optional().nullable(),
  attempts: z.number().optional(),
  processedAt: z.coerce.date().optional().nullable(),
  claimToken: z.string().optional().nullable(),
}) as unknown as z.Schema<StripeEventInitializer>

export const stripeEventMutator = z.object({
  id: stripeEventId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  type: z.string().optional(),
  payload: z.unknown().optional(),
  status: stripeEventStatusId.optional(),
  errorMessage: z.string().optional().nullable(),
  attempts: z.number().optional(),
  processedAt: z.coerce.date().optional().nullable(),
  claimToken: z.string().optional().nullable(),
}) as unknown as z.Schema<StripeEventMutator>
